import React, { useState } from 'react';
import {
  Avatar,
  Button,
  Chip,
  SendIcon,
  Text,
  Tooltip,
} from '@billda/design-system';
import { useNavigate } from 'react-router-dom';
import { PlatformOrganisationDto } from '../../api/platformApi';
import { routeBuilders } from '../../../../routes';
import { useResendOwnerInvitation } from '../../hooks/useResendOwnerInvitation';
import { useSnackbarStore } from '../../../../shared/stores/snackbarStore';
import { ResendOwnerInvitationDialog } from './ResendOwnerInvitationDialog';

interface PlatformOrganisationListItemProps {
  organisation: PlatformOrganisationDto;
}

function getStatusChipProps(status: string) {
  switch (status) {
    case 'Active':
      return { label: 'Active', color: 'success' as const };
    case 'InvitationSent':
      return { label: 'Invited', color: 'warning' as const };
    case 'Expired':
      return { label: 'Expired', color: 'error' as const };
    case 'Inactive':
      return { label: 'Inactive', color: 'default' as const };
    default:
      return { label: status, color: 'default' as const };
  }
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  });
}

export const PlatformOrganisationListItem = React.memo(
  function PlatformOrganisationListItem({
    organisation,
  }: PlatformOrganisationListItemProps) {
    const navigate = useNavigate();
    const showSnackbar = useSnackbarStore(state => state.showSnackbar);
    const chipProps = getStatusChipProps(organisation.invitationStatus);
    const [resendDialogOpen, setResendDialogOpen] = useState(false);
    const { mutate: resendInvite, isPending: isResending } =
      useResendOwnerInvitation();

    const handleResendClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setResendDialogOpen(true);
    };

    const handleResendConfirm = () => {
      resendInvite(organisation.id, {
        onSuccess: () => {
          setResendDialogOpen(false);
          showSnackbar('Invitation resent', 'success');
        },
      });
    };

    const handleResendCancel = () => {
      setResendDialogOpen(false);
    };

    return (
      <div
        role="button"
        tabIndex={0}
        data-testid="platform-organisation-row"
        onClick={() =>
          navigate(routeBuilders.platform.organisationDetail(organisation.id))
        }
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(
              routeBuilders.platform.organisationDetail(organisation.id)
            );
          }
        }}
        className="flex cursor-pointer items-center gap-3 border-b border-border-default px-4 py-3 transition-colors hover:bg-surface-raised sm:px-6"
      >
        {/* Avatar */}
        <Avatar
          size={32}
          className="shrink-0 bg-surface-raised text-[0.75rem] font-semibold text-content-secondary"
        >
          {getInitials(organisation.name)}
        </Avatar>

        {/* Name — always visible */}
        <div className="min-w-0 flex-1">
          <Text
            as="span"
            variant="body2"
            noWrap
            className="block font-semibold"
          >
            {organisation.name}
          </Text>
        </div>

        {/* Owner + Created — hidden on mobile */}
        <div className="hidden shrink-0 items-center gap-4 sm:flex">
          <Text
            as="span"
            variant="body2"
            color="secondary"
            noWrap
            className="block w-[240px]"
          >
            {organisation.ownerEmail}
          </Text>
          <Text
            as="span"
            variant="body2"
            color="secondary"
            className="w-[100px]"
          >
            {formatDate(organisation.createdAt)}
          </Text>
        </div>

        {/* Status chip — always visible */}
        <div className="flex w-[90px] justify-end">
          <Chip
            label={chipProps.label}
            color={chipProps.color}
            size="sm"
            className="text-[0.7rem]"
          />
        </div>

        {/* Resend action — reserves width so rows stay aligned */}
        <div className="flex w-10 justify-end">
          {organisation.canResendOwnerInvite && (
            <Tooltip content="Resend invitation">
              <Button
                variant="ghost"
                size="icon"
                className="h-[30px] w-[30px]"
                onClick={handleResendClick}
                aria-label="Resend invitation"
                data-testid="resend-owner-invitation-button"
              >
                <SendIcon aria-hidden="true" className="size-4" />
              </Button>
            </Tooltip>
          )}
        </div>

        <ResendOwnerInvitationDialog
          open={resendDialogOpen}
          email={organisation.ownerEmail}
          organisationName={organisation.name}
          isLoading={isResending}
          onConfirm={handleResendConfirm}
          onCancel={handleResendCancel}
        />
      </div>
    );
  }
);
